import { Component } from '@theme/component';
/**
 * A custom element that manages order form price calculation.
 * Detects quantity changes and updates the total price display.
 *
 * @typedef {object} ProductFormOrderRefs
 * @property {HTMLElement} totalPrice - The element displaying the total price.
 * @property {HTMLInputElement} quantity - The quantity input element.
 * @property {HTMLInputElement} [socialLink] - The visible social link input.
 * @property {HTMLInputElement} [socialLinkProp] - The hidden social link property input.
 *
 * @extends Component<ProductFormOrderRefs>
 */
export class ProductFormOrderComponent extends Component {
  requiredRefs = ['totalPrice', 'quantity'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #bundleDiscount = 0;

  /** @type {number} */
  #bundleProductsTotal = 0;

  connectedCallback() {
    super.connectedCallback();

    const { quantity } = this.refs;

    if (quantity instanceof HTMLInputElement) {
      this.#abortController = new AbortController();

      // Listen for input and change events on quantity input
      quantity.addEventListener(
        'input',
        this.#handleQuantityChange,
        this.#abortController ? { signal: this.#abortController.signal } : undefined
      );
      quantity.addEventListener(
        'change',
        this.#handleQuantityChange,
        this.#abortController ? { signal: this.#abortController.signal } : undefined
      );

      // Listen for bundle discount updates
      this.addEventListener(
        'bundle-discount:update',
        this.#handleBundleDiscountUpdate,
        this.#abortController ? { signal: this.#abortController.signal } : undefined
      );

      // Sync social link -> hidden socialLinkProp if both refs exist
      const { socialLink, socialLinkProp } = /** @type {ProductFormOrderRefs} */ (this.refs);
      if (socialLink instanceof HTMLInputElement && socialLinkProp instanceof HTMLInputElement) {
        const syncSocialLinkProp = () => {
          socialLinkProp.value = socialLink.value || '';
        };

        const controller = this.#abortController;
        const options = controller ? { signal: controller.signal } : undefined;

        socialLink.addEventListener('input', syncSocialLinkProp, options);
        socialLink.addEventListener('change', syncSocialLinkProp, options);

        // Initial sync
        syncSocialLinkProp();
      }

      // Initial calculation
      this.#updateTotalPrice();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * Handles quantity change event
   * @param {Event} event - The input/change event
   */
  #handleQuantityChange = (event) => {
    event.stopPropagation();
    this.#updateTotalPrice();
  };

  /**
   * Handles bundle discount update event
   * @param {CustomEvent} event - The bundle-discount:update event
   */
  #handleBundleDiscountUpdate = (event) => {
    const { detail } = event;

    const {
      discount = 0,
      selectedCount = 0,
      selectedProducts = [],
      bundleData = null,
    } = detail;
    // Store discount percentage
    this.#bundleDiscount = parseInt(discount, 10) || 0;

    // Calculate total price of selected bundle products (in cents)
    // selectedProducts contains price as string (money_without_currency format)
    this.#bundleProductsTotal = selectedProducts.reduce((total, product) => {
      // Parse price string (e.g., "10.00" or "10") to cents
      const priceStr = product.price || '0';
      const priceInDollars = parseFloat(priceStr) || 0;
      const priceInCents = Math.round(priceInDollars * 100);
      return total + priceInCents;
    }, 0);

    // Recalculate total price
    this.#updateTotalPrice();
    this.#updateBundleData(bundleData);
  };

  #updateBundleData(bundleData) {
    if (!bundleData) return;

    // bundleData is an object like:
    // {
    //   _bundle_discount_items: "...",
    //   _bundle_discount_value: "20",
    //   _bundle_discount_percent: "20%"
    // }
    const form = this.closest('form');
    if (!form) return;

    Object.entries(bundleData).forEach(([key, value]) => {
      if (value == null) return;

      const stringValue = String(value);
      const inputName = `properties[${key}]`;

      /** @type {HTMLInputElement | null} */
      const existing = form.querySelector(`input[name="${inputName}"]`);

      if (existing instanceof HTMLInputElement) {
        existing.value = stringValue;
      } else {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        input.value = stringValue;
        form.appendChild(input);
      }
    });
  }

  /**
   * Updates the total price based on variant price, quantity, bundle products, and discount
   */
  #updateTotalPrice() {
    const { totalPrice, quantity } = this.refs;

    if (!totalPrice || !(quantity instanceof HTMLInputElement)) return;

    // Get variant price from data-variant-price attribute of the component (in cents)
    const variantPrice = parseInt(this.dataset.variantPrice || '0', 10);

    // Get quantity value
    const quantityValue = parseInt(quantity.value || '1', 10);

    // Calculate main product total (in cents)
    const mainProductTotal = variantPrice * quantityValue;

    // Calculate subtotal: main product + bundle products
    const subtotalInCents = mainProductTotal + this.#bundleProductsTotal;

    // Apply discount if available
    let finalTotalInCents = subtotalInCents;
    if (this.#bundleDiscount > 0 && this.#bundleProductsTotal > 0) {
      // Apply discount percentage to the entire order
      const discountAmount = Math.round((subtotalInCents * this.#bundleDiscount) / 100);
      finalTotalInCents = subtotalInCents - discountAmount;
    }

    // Format and update total price
    const formattedPrice = this.#formatMoney(finalTotalInCents);
    totalPrice.textContent = formattedPrice;

    // Update quantity display in order summary if exists
    const quantityDisplay = this.querySelector('.order-summary-quantity');
    if (quantityDisplay) {
      quantityDisplay.textContent = quantityValue.toString();
    }
  }

  /**
   * Formats money value using Shopify's formatMoney if available, otherwise uses simple formatting
   * @param {number} cents - The price in cents
   * @returns {string} The formatted price string
   */
  #formatMoney(cents) {
    // Try to use Shopify.formatMoney if available
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    // Fallback: Format using money format template if available
    const moneyFormatTemplate = this.querySelector('template[ref="moneyFormat"]');
    if (moneyFormatTemplate instanceof HTMLTemplateElement) {
      const template = moneyFormatTemplate.content.textContent || '{{amount}}';
      return this.#formatMoneyWithTemplate(cents, template);
    }

    // Simple fallback formatting
    const dollars = cents / 100;
    const formatted = dollars.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `$${formatted}`;
  }

  /**
   * Formats money using Shopify money format template
   * Replicates the implementation of the `money` liquid filter
   * @param {number} cents - The price in cents
   * @param {string} template - The money format template (e.g., "{{amount}}")
   * @returns {string} The formatted price string
   */
  #formatMoneyWithTemplate(cents, template) {
    const DEFAULT_CURRENCY_DECIMALS = 2;
    const CURRENCY_DECIMALS = {
      'JPY': 0,
      'KRW': 0,
      'VND': 0,
      'CLP': 0,
    };

    const currency = this.dataset.currencyCode || '';
    const currencyUpper = currency.toUpperCase();

    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
      if (placeholder === 'currency') {
        return currency;
      }

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currencyUpper] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Use defaults
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(cents, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} cents - The price in cents
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision (decimal places)
   * @returns {string} The formatted money value
   */
  #formatCents(cents, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (cents / 100).toFixed(precision);
    let [integerPart, decimalPart] = roundedNumber.split('.');

    if (!integerPart) integerPart = '0';
    if (!decimalPart) decimalPart = '';

    // Add thousands separator
    integerPart = integerPart.replace(/\d(?=(\d{3})+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? integerPart : integerPart + decimalSeparator + decimalPart.padEnd(precision, '0');
  }
}

if (!customElements.get('product-form-order-component')) {
  customElements.define('product-form-order-component', ProductFormOrderComponent);
}
